/**
 * @file The below-`md` body of `ResponsiveTable`: the same `columns` and
 * `dataSource` as one card per row, with antd's row behaviors carried over.
 * Consumer: `ResponsiveTable` only. It mirrors antd's own rules so a caller
 * cannot tell the two apart: a compare-function sorter sorts here and `true`
 * leaves sorting to the caller's `onChange`; sorting returns to page one; a
 * `total` larger than the rows means the rows are already one page; selection,
 * expansion, sort and page are each controlled when the caller passes the value
 * and kept here otherwise. Selection and expand controls stop their click so it
 * never reaches the card's row click. `scroll.y` caps the card list's height and
 * scrolls it; a `placement` of `'none'` drops the pager; hidden columns are
 * neither body lines nor sort options. Column filters and `summary` are not drawn;
 * `cardSummary` is, as one more card after the row cards and before the table's
 * `footer` and pager, named "<aria-label> summary".
 */
import { useState, type Key, type MouseEvent, type ReactNode } from 'react';
import { Button, Checkbox, Empty, Pagination, Radio, Select, Spin, Typography, theme } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import type { AnyObject } from 'antd/es/_util/type';
import type {
  ColumnsType,
  CompareFn,
  RowSelectMethod,
  SorterResult,
  SortOrder,
  TablePaginationConfig,
} from 'antd/es/table/interface';

import FlexBox from '../FlexBox';
import FlexCard from '../FlexCard';
import FlexCol from '../FlexCol';
import ResponsiveTableCard from './ResponsiveTableCard';
import type { ResponsiveTableLeaf, ResponsiveTableProps } from './ResponsiveTable.types';

const { Text } = Typography;

const DEFAULT_PAGE_SIZE = 10;

const DIRECTION_LABELS: Record<'ascend' | 'descend', string> = {
  ascend: 'Ascending',
  descend: 'Descending',
};

const leavesOf = <T extends AnyObject>(
  columns: ColumnsType<T>,
  position: string,
): ResponsiveTableLeaf<T>[] =>
  columns.flatMap((column, at) => {
    if ('children' in column) return leavesOf(column.children, `${position}${at}-`);
    const { dataIndex } = column;
    const path: readonly PropertyKey[] =
      dataIndex === undefined || dataIndex === null || dataIndex === ''
        ? []
        : Array.isArray(dataIndex)
          ? dataIndex
          : [dataIndex];
    const key =
      column.key !== undefined
        ? String(column.key)
        : path.length > 0
          ? path.map(String).join('.')
          : `${position}${at}`;
    return [{ key, path, column }];
  });

const compareOf = <T extends AnyObject>(
  leaf: ResponsiveTableLeaf<T> | undefined,
): CompareFn<T> | undefined => {
  const sorter = leaf?.column.sorter;
  if (typeof sorter === 'function') return sorter;
  if (typeof sorter === 'object') return sorter.compare;
  return undefined;
};

const ResponsiveTableCards = <T extends AnyObject>({
  cardTitle,
  cardSummary,
  'aria-label': ariaLabel,
  columns = [],
  dataSource = [],
  rowKey,
  onRow,
  rowSelection,
  expandable,
  pagination,
  loading,
  locale,
  onChange,
  sortDirections,
  title,
  footer,
  scroll,
  className,
  style,
}: ResponsiveTableProps<T>): ReactNode => {
  const { token } = theme.useToken();

  const leaves = leavesOf(columns, '');
  const titleLeaf =
    leaves.find((leaf) => cardTitle !== undefined && leaf.key === String(cardTitle)) ??
    leaves.find((leaf) => !leaf.column.hidden);
  const bodyLeaves = leaves.filter((leaf) => leaf !== titleLeaf && !leaf.column.hidden);
  const sortable = leaves.filter((leaf) => Boolean(leaf.column.sorter) && !leaf.column.hidden);

  const keyByRecord = new Map<T, Key>(
    dataSource.map((record, index) => [
      record,
      typeof rowKey === 'function' ? rowKey(record, index) : (record[rowKey ?? 'key'] ?? index),
    ]),
  );
  const recordKey = (record: T): Key => keyByRecord.get(record) ?? dataSource.indexOf(record);

  const directionsOf = (leaf: ResponsiveTableLeaf<T>): ('ascend' | 'descend')[] =>
    (leaf.column.sortDirections ?? sortDirections ?? ['ascend', 'descend']).filter(
      (order): order is 'ascend' | 'descend' => order !== null,
    );

  const [sortState, setSortState] = useState<{ key?: string; order?: SortOrder }>(() => {
    const preset = leaves.find((leaf) => leaf.column.defaultSortOrder);
    return { key: preset?.key, order: preset?.column.defaultSortOrder };
  });
  const sortControlled = sortable.some((leaf) => leaf.column.sortOrder !== undefined);
  const activeLeaf = sortControlled
    ? sortable.find((leaf) => leaf.column.sortOrder)
    : sortable.find((leaf) => leaf.key === sortState.key);
  const activeOrder = (sortControlled ? activeLeaf?.column.sortOrder : sortState.order) ?? undefined;

  const sortRows = (leaf: ResponsiveTableLeaf<T> | undefined, order: SortOrder | undefined): readonly T[] => {
    const compare = compareOf(leaf);
    if (!compare || !order) return dataSource;
    return [...dataSource].sort((a, b) => {
      const result = compare(a, b, order);
      return order === 'descend' ? -result : result;
    });
  };
  const sorted = sortRows(activeLeaf, activeOrder);

  const paginationConfig: TablePaginationConfig | undefined =
    pagination === false ? undefined : (pagination ?? {});
  const [pageState, setPageState] = useState({
    current: paginationConfig?.defaultCurrent ?? 1,
    pageSize: paginationConfig?.defaultPageSize ?? DEFAULT_PAGE_SIZE,
  });
  const pageSize = paginationConfig?.pageSize ?? pageState.pageSize;
  const total = paginationConfig?.total ?? sorted.length;
  const current = Math.min(
    paginationConfig?.current ?? pageState.current,
    Math.max(1, Math.ceil(total / pageSize)),
  );
  const pageData: readonly T[] = !paginationConfig
    ? sorted
    : sorted.length < total && sorted.length <= pageSize
      ? sorted
      : sorted.slice((current - 1) * pageSize, current * pageSize);

  const tablePagination = (page: number, size: number): TablePaginationConfig =>
    paginationConfig ? { ...paginationConfig, current: page, pageSize: size, total } : {};

  const sorterOf = (leaf: ResponsiveTableLeaf<T> | undefined, order: SortOrder | undefined): SorterResult<T> => {
    const field = (leaf?.path ?? []).filter(
      (segment): segment is string | number => typeof segment !== 'symbol',
    );
    return {
      column: order ? leaf?.column : undefined,
      columnKey: leaf?.column.key ?? leaf?.key,
      field: field.length === 0 ? undefined : field.length === 1 ? field[0] : field,
      order,
    };
  };

  const changeSort = (key: string | undefined, order: SortOrder | undefined): void => {
    const leaf = sortable.find((candidate) => candidate.key === key);
    const nextOrder = leaf
      ? (directionsOf(leaf).find((direction) => direction === order) ?? directionsOf(leaf)[0])
      : undefined;
    if (!sortControlled) setSortState({ key: leaf?.key, order: nextOrder });
    if (paginationConfig?.current === undefined) setPageState((state) => ({ ...state, current: 1 }));
    onChange?.(tablePagination(1, pageSize), {}, sorterOf(leaf ?? activeLeaf, nextOrder), {
      action: 'sort',
      currentDataSource: [...sortRows(leaf, nextOrder)],
    });
  };

  const changePage = (page: number, size: number): void => {
    if (paginationConfig?.current === undefined || paginationConfig.pageSize === undefined) {
      setPageState({ current: page, pageSize: size });
    }
    paginationConfig?.onChange?.(page, size);
    onChange?.(tablePagination(page, size), {}, sorterOf(activeLeaf, activeOrder), {
      action: 'paginate',
      currentDataSource: [...sorted],
    });
  };

  const [selectionState, setSelectionState] = useState<readonly Key[]>(
    rowSelection?.defaultSelectedRowKeys ?? [],
  );
  const selectedKeys = rowSelection?.selectedRowKeys ?? selectionState;
  const rowsOf = (keys: readonly Key[]): T[] =>
    dataSource.filter((record) => keys.includes(recordKey(record)));
  const select = (keys: Key[], type: RowSelectMethod): void => {
    if (!rowSelection?.selectedRowKeys) setSelectionState(keys);
    rowSelection?.onChange?.(keys, rowsOf(keys), { type });
  };
  const toggleRow = (record: T, checked: boolean, nativeEvent: Event): void => {
    const key = recordKey(record);
    const keys =
      rowSelection?.type === 'radio'
        ? checked
          ? [key]
          : []
        : checked
          ? [...selectedKeys.filter((selected) => selected !== key), key]
          : selectedKeys.filter((selected) => selected !== key);
    rowSelection?.onSelect?.(record, checked, rowsOf(keys), nativeEvent);
    select(keys, 'single');
  };

  const enabledPageKeys = pageData
    .filter((record) => !rowSelection?.getCheckboxProps?.(record).disabled)
    .map(recordKey);
  const pageChecked = enabledPageKeys.filter((key) => selectedKeys.includes(key));
  const selectAll =
    rowSelection && rowSelection.type !== 'radio' && !rowSelection.hideSelectAll ? (
      <Checkbox
        {...rowSelection.getTitleCheckboxProps?.()}
        aria-label={rowSelection.getTitleCheckboxProps?.()['aria-label'] ?? `Select all ${ariaLabel ?? 'rows'}`}
        disabled={enabledPageKeys.length === 0}
        checked={enabledPageKeys.length > 0 && pageChecked.length === enabledPageKeys.length}
        indeterminate={pageChecked.length > 0 && pageChecked.length < enabledPageKeys.length}
        onChange={(event) =>
          event.target.checked
            ? select(
                [...selectedKeys, ...enabledPageKeys.filter((key) => !selectedKeys.includes(key))],
                'all',
              )
            : select(
                selectedKeys.filter((key) => !enabledPageKeys.includes(key)),
                'none',
              )
        }
      >
        Select all
      </Checkbox>
    ) : undefined;

  const [expandedState, setExpandedState] = useState<readonly Key[]>(() =>
    expandable?.defaultExpandAllRows
      ? dataSource.map(recordKey)
      : (expandable?.defaultExpandedRowKeys ?? []),
  );
  const expandedKeys = expandable?.expandedRowKeys ?? expandedState;
  const toggleExpanded = (record: T, event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    const key = recordKey(record);
    const expanded = !expandedKeys.includes(key);
    const next = expanded ? [...expandedKeys, key] : expandedKeys.filter((open) => open !== key);
    if (!expandable?.expandedRowKeys) setExpandedState(next);
    expandable?.onExpand?.(expanded, record);
    expandable?.onExpandedRowsChange?.(next);
  };

  const spinProps = typeof loading === 'object' ? { spinning: true, ...loading } : { spinning: Boolean(loading) };
  const emptyText =
    typeof locale?.emptyText === 'function'
      ? locale.emptyText()
      : (locale?.emptyText ?? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />);

  const cards = (
    <FlexCol
      role="list"
      aria-label={ariaLabel}
      gap={10}
      style={scroll?.y === undefined ? undefined : { maxHeight: scroll.y, overflowY: 'auto' }}
    >
      {pageData.map((record, index) => {
        const key = recordKey(record);
        const checkboxProps = rowSelection?.getCheckboxProps?.(record);
        const checked = selectedKeys.includes(key);
        const selectLabel = checkboxProps?.['aria-label'] ?? `Select row ${String(key)}`;
        const selectControl = !rowSelection ? undefined : rowSelection.type === 'radio' ? (
          <Radio
            aria-label={selectLabel}
            disabled={checkboxProps?.disabled}
            checked={checked}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => toggleRow(record, event.target.checked, event.nativeEvent)}
          />
        ) : (
          <Checkbox
            {...checkboxProps}
            aria-label={selectLabel}
            checked={checked}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => toggleRow(record, event.target.checked, event.nativeEvent)}
          />
        );
        const canExpand = expandable?.rowExpandable?.(record) ?? true;
        const expanded = expandedKeys.includes(key);
        const expandControl = !expandable?.expandedRowRender ? undefined : expandable.expandIcon ? (
          expandable.expandIcon({
            prefixCls: 'ant-table',
            expanded,
            record,
            expandable: canExpand,
            onExpand: toggleExpanded,
          })
        ) : (
          <Button
            type="link"
            size="small"
            disabled={!canExpand}
            icon={expanded ? <DownOutlined /> : <RightOutlined />}
            aria-label={`${expanded ? 'Hide' : 'Show'} details of row ${String(key)}`}
            onClick={(event) => toggleExpanded(record, event)}
          >
            {expanded ? 'Hide details' : 'Show details'}
          </Button>
        );
        return (
          <ResponsiveTableCard<T>
            key={key}
            record={record}
            index={index}
            titleLeaf={titleLeaf}
            bodyLeaves={bodyLeaves}
            rowAttributes={onRow?.(record, index) ?? {}}
            selectControl={selectControl}
            expandControl={expandControl}
            expandedContent={
              expanded && canExpand ? expandable?.expandedRowRender?.(record, index, 0, true) : undefined
            }
          />
        );
      })}
    </FlexCol>
  );

  return (
    <FlexCol gap={10} className={className} style={{ width: '100%', alignItems: 'stretch', ...style }}>
      {title?.(pageData)}
      {(selectAll || sortable.length > 0) && (
        <FlexBox noGrow justifyStart wrap>
          {selectAll}
          {sortable.length > 0 && (
            <FlexBox noGrow justifyStart gap={5}>
              <Text type="secondary">Sort by</Text>
              <Select<string>
                size="small"
                allowClear
                aria-label={`Sort ${ariaLabel ?? 'rows'} by`}
                placeholder="Column"
                popupMatchSelectWidth={false}
                style={{ minWidth: token.controlHeight * 4 }}
                value={activeOrder ? activeLeaf?.key : undefined}
                options={sortable.map((leaf) => ({
                  value: leaf.key,
                  label: typeof leaf.column.title === 'function' ? leaf.column.title({}) : leaf.column.title,
                }))}
                onChange={(key: string | undefined) => changeSort(key, activeOrder)}
              />
              <Select<'ascend' | 'descend'>
                size="small"
                aria-label={`Sort ${ariaLabel ?? 'rows'} direction`}
                popupMatchSelectWidth={false}
                disabled={!activeLeaf || !activeOrder}
                value={activeOrder ?? 'ascend'}
                options={(activeLeaf ? directionsOf(activeLeaf) : ['ascend' as const, 'descend' as const]).map(
                  (order) => ({ value: order, label: DIRECTION_LABELS[order] }),
                )}
                onChange={(order) => changeSort(activeLeaf?.key, order)}
              />
            </FlexBox>
          )}
        </FlexBox>
      )}
      {spinProps.spinning && pageData.length === 0 ? (
        <FlexBox justifyCenter style={{ width: '100%', boxSizing: 'border-box', padding: token.paddingLG }}>
          <Spin {...spinProps} />
        </FlexBox>
      ) : pageData.length === 0 ? (
        <FlexBox justifyCenter>{emptyText}</FlexBox>
      ) : (
        <Spin {...spinProps}>{cards}</Spin>
      )}
      {cardSummary !== undefined && cardSummary !== null && (
        <FlexCard role="group" aria-label={`${ariaLabel ?? 'Table'} summary`} style={{ width: '100%' }}>
          {cardSummary}
        </FlexCard>
      )}
      {footer?.(pageData)}
      {paginationConfig && !paginationConfig.placement?.includes('none') && (
        <FlexBox noGrow justifyEnd>
          <Pagination
            size="small"
            {...paginationConfig}
            current={current}
            pageSize={pageSize}
            total={total}
            onChange={changePage}
          />
        </FlexBox>
      )}
    </FlexCol>
  );
};

export default ResponsiveTableCards;
